import {ReactNode} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Typography} from '@mui/material';

import {surveyStatisticBars} from './SurveyStatisticBars.colors';
import {SurveyStatisticOmittedSection} from './SurveyStatisticOmittedSection';
import {greenColors, neutral} from '../../../../../../../layouts/theme/designSystem';

export type DistributionBarItem = {
  label: string;
  percentage: number;
  color?: string;
};

interface Props {
  title: string;
  icon: ReactNode;
  items: DistributionBarItem[];
  isOmitted?: boolean;
}

const normalizePercentage = (value?: number) => {
  if (!value || !Number.isFinite(value)) return 0;
  return Math.min(Math.max(value, 0), 100);
};

const formatPercentage = (value?: number) => {
  const percentage = normalizePercentage(value);
  return `${percentage}%`;
};

export const PercentageDistributionBars = ({title, icon, items, isOmitted = false}: Props) => {
  const {t} = useTranslation();

  const cardBackgroundColor = surveyStatisticBars.track;
  const primaryTextColor = greenColors[700];
  const secondaryTextColor = neutral[700];
  const tertiaryTextColor = neutral[500];

  if (isOmitted) {
    return (
      <SurveyStatisticOmittedSection
        title={title}
        icon={icon}
        message={t('companySurveys.details.notCollectedInSurvey')}
      />
    );
  }

  return (
    <Box>
      <Box
        sx={{
          alignItems: 'center',
          color: greenColors[700],
          display: 'flex',
          gap: '5px',
          marginBottom: '12px',
        }}
      >
        {icon}
        <Typography
          sx={{
            color: 'inherit',
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.04em',
            lineHeight: 1.4,
            textTransform: 'uppercase',
          }}
        >
          {title}
        </Typography>
      </Box>
      {items.map((item) => {
        const percentage = normalizePercentage(item.percentage);

        return (
          <Box
            key={item.label}
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: '8px',
              marginBottom: '5px',
            }}
          >
            <Typography
              sx={{
                color: secondaryTextColor,
                flexShrink: 0,
                fontSize: '12px',
                lineHeight: 1.4,
                width: '90px',
              }}
            >
              {item.label}
            </Typography>
            <Box
              sx={{
                backgroundColor: cardBackgroundColor,
                borderRadius: '99px',
                flex: 1,
                height: '6px',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  backgroundColor: item.color ?? surveyStatisticBars.fill,
                  borderRadius: '99px',
                  height: '100%',
                  width: `${percentage}%`,
                }}
              />
            </Box>
            <Typography
              sx={{
                color: percentage > 0 ? primaryTextColor : tertiaryTextColor,
                flexShrink: 0,
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: 1.4,
                textAlign: 'right',
                width: '42px',
              }}
            >
              {formatPercentage(percentage)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
